import React, { useCallback, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { vizMarkdownComponents } from '../../utils/markdownComponents';
import { WorkflowIcon } from './WorkflowIconPicker';

// ---------------------------------------------------------------------------
// Copy button
// ---------------------------------------------------------------------------

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  }, [text]);
  return (
    <button
      onClick={handleCopy}
      title="Copy to clipboard"
      style={{
        padding: '2px 7px',
        fontSize: '0.65rem',
        border: '1px solid var(--border-color)',
        borderRadius: 4,
        background: 'transparent',
        color: copied ? 'var(--success-text-color, #065f46)' : 'var(--text-secondary)',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Single workflow run card
// ---------------------------------------------------------------------------

/**
 * Renders one workflow Q&A pair:
 *  - User question bubble at top
 *  - Workflow name badge
 *  - Output area: streaming indicator while running, markdown when done,
 *    error message on failure, or "Cancelled" on cancellation.
 *
 * @param {object} entry           - workflowRunHistory entry
 * @param {object} activeRunState  - workflowActiveRuns[entry.workflowId] if
 *                                   entry.runId matches the active run, else null
 */
function WorkflowRunCard({ entry, activeRunState }) {
  const { question, workflowName, workflowIcon, status, output_json, error_message } = entry;

  // Determine whether this card is the currently-streaming run
  const isActiveRun =
    activeRunState &&
    activeRunState.runId === entry.runId &&
    (activeRunState.status === 'pending' || activeRunState.status === 'running');

  // Build display text from output_json (values may be string or object)
  const outputText = React.useMemo(() => {
    const src = output_json || (activeRunState?.output_json);
    if (!src || typeof src !== 'object') return null;
    const values = Object.values(src).filter(v => v != null);
    if (values.length === 0) return null;
    return values
      .map(v => (typeof v === 'string' ? v : JSON.stringify(v, null, 2)))
      .join('\n\n');
  }, [output_json, activeRunState]);

  const displayStatus = isActiveRun ? activeRunState.status : status;

  return (
    <div style={{ marginBottom: '1.5rem' }} data-indicator="input" data-indicator-key={entry.runId || entry.id}>
      {/* User question */}
      {question && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '0.5rem',
        }}>
          <div style={{
            maxWidth: '80%',
            background: 'var(--primary-color, #636cff)',
            color: '#fff',
            borderRadius: '12px 12px 2px 12px',
            padding: '8px 14px',
            fontSize: '0.9rem',
            lineHeight: 1.5,
            wordBreak: 'break-word',
          }}>
            {question}
          </div>
        </div>
      )}

      {/* Workflow output card */}
      <div
        {...(displayStatus === 'completed' && outputText ? { 'data-indicator': 'output', 'data-indicator-key': `out-${entry.runId || entry.id}` } : {})}
        style={{
          background: 'var(--panel-background, var(--input-background))',
          border: '1px solid var(--border-color)',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        {/* Card header: workflow name + copy button */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 12px',
          borderBottom: '1px solid var(--border-color)',
          background: 'var(--input-background)',
        }}>
          <span style={{
            fontSize: '0.72rem',
            fontWeight: 600,
            color: 'var(--text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}>
            <WorkflowIcon iconKey={workflowIcon} size={13} color="var(--text-secondary)" />
            {workflowName}
          </span>
          {outputText && <CopyButton text={outputText} />}
        </div>

        {/* Card body */}
        <div style={{ padding: '12px 14px' }}>

          {/* Streaming / running indicator */}
          {(displayStatus === 'pending' || displayStatus === 'running') && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: '0.82rem',
              color: 'var(--text-secondary)',
            }}>
              <span style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: 'var(--primary-color, #636cff)',
                animation: 'wf-pulse 1.4s ease-in-out infinite',
                flexShrink: 0,
              }} />
              Running {workflowName}…
            </div>
          )}

          {/* Latest live sub-agent progress line per running node —
              parallel execution can have several nodes running at once */}
          {isActiveRun && activeRunState.node_results_json && (() => {
            const lines = Object.entries(
              activeRunState.node_results_json,
            )
              .filter(
                ([, r]) =>
                  r?.status === 'running' &&
                  Array.isArray(r?.progress) &&
                  r.progress.length > 0,
              )
              .map(([nodeId, r]) => {
                const last = r.progress[r.progress.length - 1];
                return { nodeId, msg: last?.message || last?.status };
              })
              .filter(({ msg }) => msg);
            if (lines.length === 0) return null;
            return lines.map(({ nodeId, msg }) => (
              <div
                key={nodeId}
                style={{
                  fontSize: '0.72rem',
                  color: 'var(--text-secondary)',
                  marginTop: 4,
                  paddingLeft: 16,
                }}
              >
                {msg}
              </div>
            ));
          })()}

          {/* Live node results while streaming */}
          {isActiveRun && activeRunState.node_results_json &&
            Object.keys(activeRunState.node_results_json).length > 0 && (
            <div style={{ marginTop: 8 }}>
              {Object.entries(activeRunState.node_results_json)
                .filter(([, r]) => r?.status === 'completed' && r?.content)
                .slice(0, 3) // show at most 3 intermediate nodes to keep it concise
                .map(([nodeId, r]) => (
                  <div
                    key={nodeId}
                    style={{
                      fontSize: '0.72rem',
                      color: 'var(--text-secondary)',
                      marginTop: 4,
                      paddingLeft: 16,
                      borderLeft: '2px solid var(--border-color)',
                    }}
                  >
                    ✓ {nodeId}
                  </div>
                ))}
            </div>
          )}

          {/* Cancelled */}
          {displayStatus === 'cancelled' && !outputText && (
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              Run cancelled.
            </span>
          )}

          {/* Error */}
          {displayStatus === 'failed' && (
            <div style={{
              fontSize: '0.82rem',
              color: 'var(--error-text-color, #dc2626)',
              background: 'rgba(220,38,38,0.06)',
              border: '1px solid rgba(220,38,38,0.2)',
              borderRadius: 4,
              padding: '6px 10px',
            }}>
              {error_message || 'Workflow run failed.'}
            </div>
          )}

          {/* Final output */}
          {outputText && (
            <div className="wf-run-markdown">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeSanitize]}
                components={vizMarkdownComponents}
              >
                {outputText}
              </ReactMarkdown>
            </div>
          )}

          {/* No output but completed */}
          {displayStatus === 'completed' && !outputText && (
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              Workflow completed with no output.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

/**
 * WorkflowChatView — renders the workflow Q&A conversation in the main panel.
 *
 * @param {Array}  workflowRunHistory  - ordered list of run entries
 * @param {object} workflowActiveRuns  - {[workflowId]: activeRunState} from useWorkflow
 * @param {string} activeWorkflowId    - currently selected workflow ID
 */
export default function WorkflowChatView({
  workflowRunHistory = [],
  workflowActiveRuns = {},
  activeWorkflowId = null,
}) {
  // Empty state is handled by the centred headline in MainPanel — render nothing here.
  if (workflowRunHistory.length === 0) {
    return null;
  }

  return (
    <div style={{ padding: '1rem 0 0.5rem' }}>
      {workflowRunHistory.map((entry) => {
        // Resolve live run state for this entry (only for the latest run of
        // a given workflow, since activeRuns is keyed by workflowId).
        const liveState = workflowActiveRuns[entry.workflowId];
        const activeRunState =
          liveState && liveState.runId === entry.runId ? liveState : null;

        return (
          <WorkflowRunCard
            key={entry.id}
            entry={entry}
            activeRunState={activeRunState}
          />
        );
      })}
    </div>
  );
}
